/**
 * BroadcastChannel Debug Logger
 *
 * Provides comprehensive debugging for GM-Player sync communications.
 * Logs all messages sent/received with timestamps, payloads, and state changes.
 */

/**
 * Debug log entry structure
 */
class DebugLogEntry {
  constructor(direction, type, data, timestamp = Date.now()) {
    this.id = `${timestamp}-${Math.random().toString(36).substr(2, 9)}`;
    this.timestamp = timestamp;
    this.direction = direction; // 'sent' or 'received'
    this.type = type; // Message type
    this.data = data; // Message payload
    this.viewMode = this.detectViewMode();
    this.sessionState = this.captureSessionState();
  }

  detectViewMode() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('view') === 'player' ? 'player' : 'gm';
  }

  captureSessionState() {
    if (!window.currentSession) return null;

    return {
      currentEventId: window.currentSession.currentEventId,
      historyLength: window.currentSession.history?.length || 0,
      undoStackLength: window.currentSession.undoStack?.length || 0,
      redoStackLength: window.currentSession.redoStack?.length || 0,
      revealedCount: this.countRevealed(),
      timeTravelEventId: window.currentSession.timeTravelEventId
    };
  }

  countRevealed() {
    if (!window.currentSession?.hexStates) return 0;

    let count = 0;
    for (const hexState of Object.values(window.currentSession.hexStates)) {
      // Use getCurrentRevealState if available
      if (window.getCurrentRevealState) {
        if (window.getCurrentRevealState(hexState)) count++;
      } else if (hexState.revealed) {
        count++;
      }
    }
    return count;
  }

  format() {
    const time = new Date(this.timestamp).toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });

    const arrow = this.direction === 'sent' ? '→' : '←';
    const view = this.viewMode === 'gm' ? 'GM' : 'PV';

    return `[${time}] ${view} ${arrow} ${this.type}`;
  }

  toConsoleArgs() {
    const style = this.direction === 'sent'
      ? 'color: #4CAF50; font-weight: bold'
      : 'color: #2196F3; font-weight: bold';

    return [
      `%c${this.format()}`,
      style,
      '\n📦 Payload:',
      this.data,
      '\n📊 Session State:',
      this.sessionState
    ];
  }
}

/**
 * Debug logger for BroadcastChannel communications
 */
export class BroadcastDebugger {
  constructor() {
    this.logs = [];
    this.maxLogs = 500; // Keep last 500 entries
    this.enabled = true;
    this.listeners = [];

    // Make available globally for console access
    window.broadcastDebugger = this;
  }

  /**
   * Log a sent message
   */
  logSent(type, data) {
    if (!this.enabled) return;

    const entry = new DebugLogEntry('sent', type, data);
    this.addEntry(entry);

    console.log(...entry.toConsoleArgs());
  }

  /**
   * Log a received message
   */
  logReceived(type, data) {
    if (!this.enabled) return;

    const entry = new DebugLogEntry('received', type, data);
    this.addEntry(entry);

    console.log(...entry.toConsoleArgs());
  }

  /**
   * Add entry to log history
   */
  addEntry(entry) {
    this.logs.push(entry);

    // Trim old entries
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Notify listeners
    this.notifyListeners(entry);
  }

  /**
   * Register listener for new log entries
   */
  onLogEntry(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all listeners
   */
  notifyListeners(entry) {
    for (const callback of this.listeners) {
      try {
        callback(entry);
      } catch (e) {
        console.error('Error in debug listener:', e);
      }
    }
  }

  /**
   * Get all logs
   */
  getLogs() {
    return [...this.logs];
  }

  /**
   * Get logs filtered by criteria
   */
  getFilteredLogs(filter = {}) {
    let filtered = this.logs;

    if (filter.direction) {
      filtered = filtered.filter(log => log.direction === filter.direction);
    }

    if (filter.type) {
      filtered = filtered.filter(log => log.type === filter.type);
    }

    if (filter.since) {
      filtered = filtered.filter(log => log.timestamp >= filter.since);
    }

    return filtered;
  }

  /**
   * Clear all logs
   */
  clear() {
    this.logs = [];
    this.notifyListeners(null); // Signal clear
  }

  /**
   * Export logs as JSON
   */
  exportLogs() {
    const data = {
      exported: new Date().toISOString(),
      viewMode: new URLSearchParams(window.location.search).get('view') || 'gm',
      logs: this.logs.map(log => ({
        id: log.id,
        timestamp: log.timestamp,
        time: new Date(log.timestamp).toISOString(),
        direction: log.direction,
        type: log.type,
        data: log.data,
        sessionState: log.sessionState
      }))
    };

    return JSON.stringify(data, null, 2);
  }

  /**
   * Download logs as file
   */
  downloadLogs() {
    const json = this.exportLogs();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const viewMode = new URLSearchParams(window.location.search).get('view') || 'gm';
    const filename = `broadcast-debug-${viewMode}-${Date.now()}.json`;

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
  }

  /**
   * Get statistics
   */
  getStats() {
    const stats = {
      total: this.logs.length,
      sent: this.logs.filter(l => l.direction === 'sent').length,
      received: this.logs.filter(l => l.direction === 'received').length,
      byType: {}
    };

    for (const log of this.logs) {
      if (!stats.byType[log.type]) {
        stats.byType[log.type] = { sent: 0, received: 0 };
      }
      stats.byType[log.type][log.direction]++;
    }

    return stats;
  }

  /**
   * Enable/disable logging
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    console.log(`BroadcastChannel debugging ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Print summary to console
   */
  printSummary() {
    const stats = this.getStats();

    console.group('📡 BroadcastChannel Debug Summary');
    console.log(`Total messages: ${stats.total}`);
    console.log(`Sent: ${stats.sent}`);
    console.log(`Received: ${stats.received}`);
    console.log('\nBy message type:');
    console.table(stats.byType);
    console.groupEnd();
  }

  /**
   * Detect potential sync issues
   */
  detectIssues() {
    const issues = [];
    const recentLogs = this.logs.slice(-50); // Check last 50 messages

    // Check for messages sent but no response
    const sentTypes = new Set();
    const receivedTypes = new Set();

    for (const log of recentLogs) {
      if (log.direction === 'sent') {
        sentTypes.add(log.type);
      } else {
        receivedTypes.add(log.type);
      }
    }

    // Check for state mismatches
    const stateLogs = recentLogs.filter(l => l.sessionState);
    if (stateLogs.length >= 2) {
      const latest = stateLogs[stateLogs.length - 1];
      const previous = stateLogs[stateLogs.length - 2];

      if (latest.sessionState && previous.sessionState) {
        const eventDiff = latest.sessionState.currentEventId - previous.sessionState.currentEventId;
        if (Math.abs(eventDiff) > 10) {
          issues.push({
            type: 'large_event_jump',
            message: `Large jump in event ID: ${eventDiff}`,
            from: previous.sessionState.currentEventId,
            to: latest.sessionState.currentEventId
          });
        }
      }
    }

    // Check for duplicate messages
    const recentIds = new Map();
    for (const log of recentLogs) {
      const key = `${log.type}-${JSON.stringify(log.data)}`;
      if (recentIds.has(key)) {
        issues.push({
          type: 'duplicate_message',
          message: `Duplicate message detected: ${log.type}`,
          count: recentIds.get(key) + 1
        });
        recentIds.set(key, recentIds.get(key) + 1);
      } else {
        recentIds.set(key, 1);
      }
    }

    if (issues.length > 0) {
      console.warn('⚠️ Potential sync issues detected:', issues);
    }

    return issues;
  }
}

// Create singleton instance
export const broadcastDebugger = new BroadcastDebugger();

/**
 * Wrap BroadcastChannel with debugging
 */
export function createDebuggedBroadcastChannel(channelName) {
  const channel = new BroadcastChannel(channelName);
  const originalPostMessage = channel.postMessage.bind(channel);

  // Wrap postMessage to log outgoing messages
  channel.postMessage = function(data) {
    broadcastDebugger.logSent(data.type || 'unknown', data);
    return originalPostMessage(data);
  };

  // Log incoming messages
  const originalAddEventListener = channel.addEventListener.bind(channel);
  channel.addEventListener('message', (event) => {
    broadcastDebugger.logReceived(event.data.type || 'unknown', event.data);
  });

  return channel;
}
